import React, { useRef, useEffect, useState } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import SaleHistoryEditor from './SaleHistoryEditor';
import styles from './SaleHistoryTimeline.module.scss';

/**
 * 📜 SALE HISTORY TIMELINE - Timeline de Histórico com Scroll Infinito
 * 
 * Features:
 * - Timeline visual com ícones e cores por tipo de ação
 * - Scroll infinito para carregar mais histórico
 * - Exibição de imagens inline
 * - HTML formatado para descrições ricas
 * - Indicador de quem fez a ação (usuário ou sistema)
 * - Editor integrado para adicionar novas entradas
 * 
 * @param {number} saleId - ID da venda
 * @param {Array} history - Array de entradas do histórico
 * @param {boolean} loading - Estado de carregamento
 * @param {boolean} hasMore - Se há mais histórico para carregar
 * @param {Function} onLoadMore - Callback para carregar mais
 * @param {Function} onActionSuccess - Callback após ação bem-sucedida
 */
const SaleHistoryTimeline = ({
  saleId,
  history = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  onActionSuccess,
  canAddComments = true  // Controla se o editor de comentarios aparece
}) => {
  const timelineEndRef = useRef(null);
  const containerRef = useRef(null);
  const [showEditorModal, setShowEditorModal] = useState(false);

  /**
   * 🌐 Tradução de chaves do action_data
   */
  const fieldTranslations = {
    // Ticket
    'ticket_id': 'ID do Ticket',
    'subject': 'Assunto',
    'category': 'Categoria',
    'priority': 'Prioridade',
    // Tracking
    'tracking_code': 'Codigo de Rastreio',
    'carrier': 'Transportadora',
    'shipping_company': 'Empresa',
    // Payment
    'payment_method': 'Metodo de Pagamento',
    'amount': 'Valor',
    'transaction_id': 'ID da Transacao',
    // Email
    'email_type': 'Tipo de Email',
    'recipient': 'Destinatario',
    // Status
    'old_status': 'Status Anterior',
    'new_status': 'Novo Status',
    'reason': 'Motivo',
    // Generic
    'notes': 'Observacoes',
    'description': 'Descricao',
    'date': 'Data',
    // Sale fields
    'is_afterpay': 'Tipo AfterPay',
    'total_amount': 'Valor Total',
    'payment_status': 'Status do Pagamento',
    'sale_status': 'Status da Venda',
    'product_name': 'Produto',
    'product_id': 'ID do Produto',
    'customer_name': 'Cliente',
    'customer_email': 'Email do Cliente',
    'affiliate_name': 'Afiliado',
    'affiliate_id': 'ID do Afiliado',
    'producer_id': 'ID do Produtor',
    'shipping_address': 'Endereco de Entrega',
    'payment_order_id': 'ID do Pedido',
    'payment_date': 'Data do Pagamento',
    'created_at': 'Data de Criacao',
    'updated_at': 'Data de Atualizacao',
    'installments': 'Parcelas',
    'plan_id': 'ID do Plano',
    'plan_name': 'Plano',
    'delivered_at': 'Data da Entrega',
    'commission_amount': 'Valor da Comissao',
    'commission_rate': 'Taxa de Comissao',
    'checkout_id': 'ID do Checkout',
    'metadata': 'Metadados',
    'customer_id': 'ID do Cliente',
    'producer_name': 'Produtor',
  };

  const valueTranslations = {
    'true': 'Sim',
    'false': 'Nao',
    'paid': 'Pago',
    'pending': 'Pendente',
    'processing': 'Processando',
    'scheduled': 'Agendado',
    'cancelled': 'Cancelado',
    'refunded': 'Reembolsado',
    'frustrated': 'Frustrado',
    'completed': 'Concluido',
    'credit_card': 'Cartao de Credito',
    'pix': 'PIX',
    'boleto': 'Boleto',
    'after_pay': 'Pagamento Apos Entrega',
  };

  const translateValue = (value) => {
    const strVal = String(value).toLowerCase();
    return valueTranslations[strVal] || value;
  };

  /**
   * 🌐 Traduzir chave do action_data
   */
  const translateField = (key) => {
    return fieldTranslations[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * 🎨 Configuração de ícones e cores por tipo de ação
   */
  const actionConfig = {
    created: {
      icon: 'mdi:cart-plus',
      color: '#3b82f6',
      label: 'Venda Criada'
    },
    status_changed: {
      icon: 'mdi:sync',
      color: '#8b5cf6',
      label: 'Status Alterado'
    },
    tracking_added: {
      icon: 'mdi:truck-delivery',
      color: '#10b981',
      label: 'Rastreio Adicionado'
    },
    payment_marked: {
      icon: 'mdi:cash-check',
      color: '#059669',
      label: 'Marcado como Pago'
    },
    note_added: {
      icon: 'mdi:note-text',
      color: '#f59e0b',
      label: 'Nota Adicionada'
    },
    image_uploaded: {
      icon: 'mdi:image',
      color: '#06b6d4',
      label: 'Imagem Enviada'
    },
    email_sent: {
      icon: 'mdi:email-send',
      color: '#6366f1',
      label: 'Email Enviado'
    },
    checkout_sent: {
      icon: 'mdi:link-variant',
      color: '#8b5cf6',
      label: 'Link de Checkout Enviado'
    },
    ticket_created: {
      icon: 'mdi:ticket-confirmation',
      color: '#f59e0b',
      label: 'Ticket Criado'
    },
    ticket_responded: {
      icon: 'mdi:message-reply',
      color: '#3b82f6',
      label: 'Ticket Respondido'
    },
    ticket_resolved: {
      icon: 'mdi:check-circle',
      color: '#10b981',
      label: 'Ticket Resolvido'
    }
  };

  /**
   * 🔍 Detectar scroll para carregar mais
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Se rolou mais de 90% e tem mais dados
      if (scrollPercentage > 0.9 && hasMore && !loading && onLoadMore) {
        onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, onLoadMore]);

  /**
   * 🎨 Obter configuração do tipo de ação
   */
  const getActionInfo = (actionType) => {
    return actionConfig[actionType] || {
      icon: 'mdi:circle',
      color: '#6b7280',
      label: 'Ação'
    };
  };

  /**
   * 📅 Formatar data/hora
   */
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * 👤 Renderizar avatar do autor
   */
  const renderAuthorAvatar = (entry) => {
    const isSystem = !entry.creator_name;
    const name = entry.creator_name || 'Sistema';

    if (isSystem) {
      return (
        <div className={styles.authorAvatar} style={{ backgroundColor: '#9ca3af' }}>
          <Icon icon="mdi:robot" style={{ fontSize: '20px', color: 'white' }} />
        </div>
      );
    }

    // Avatar com iniciais do nome
    const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    const colors = [
      '#667eea', // roxo
      '#f093fb', // rosa
      '#4facfe', // azul
      '#43e97b', // verde
      '#fa709a', // rosa escuro
      '#feca57', // amarelo
    ];
    
    // Gerar cor baseada no nome (sempre a mesma cor para o mesmo nome)
    const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    const backgroundColor = colors[colorIndex];
    
    return (
      <div className={styles.authorAvatar} style={{ backgroundColor }}>
        <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>{initials}</span>
      </div>
    );
  };

  return (
    <div className={styles.timelineContainer} ref={containerRef}>
      {/* Header */}
      <div className={styles.timelineHeader}>
        <span className={styles.headerTitle}>
          <Icon icon="mdi:timeline-text" />
          Histórico da Venda
        </span>
        <div className="d-flex align-items-center gap-2">
          <span className={styles.headerCount}>
            {history.length} {history.length === 1 ? 'registro' : 'registros'}
          </span>
          {canAddComments && (
            <button
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => setShowEditorModal(true)}
              style={{ fontSize: '12px', padding: '4px 10px' }}
            >
              <Icon icon="mdi:plus" />
              Comentar
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className={styles.timeline}>
        {history.length === 0 && !loading ? (
          <div className={styles.emptyState}>
            <Icon icon="mdi:timeline-help" />
            <p>Nenhum histórico encontrado</p>
            <span>As ações realizadas nesta venda aparecerão aqui</span>
          </div>
        ) : (
          history.map((entry, index) => {
            const actionInfo = getActionInfo(entry.action_type);
            const isLast = index === history.length - 1;

            return (
              <div key={entry.id} className={styles.timelineEntry}>
                {/* Ícone da ação */}
                <div 
                  className={styles.entryIcon}
                  style={{ backgroundColor: actionInfo.color }}
                >
                  <Icon icon={actionInfo.icon} />
                </div>

                {/* Linha conectora */}
                {!isLast && <div className={styles.connector} />}

                {/* Conteúdo */}
                <div className={styles.entryContent}>
                  {/* Cabeçalho */}
                  <div className={styles.entryHeader}>
                    <span className={styles.actionLabel}>
                      {actionInfo.label}
                    </span>
                    <span className={styles.entryDate}>
                      {formatDateTime(entry.created_at)}
                    </span>
                  </div>

                  {/* Descrição */}
                  {entry.description && (
                    <div 
                      className={styles.entryDescription}
                      dangerouslySetInnerHTML={{ __html: entry.description }}
                    />
                  )}

                  {/* Dados Adicionais (JSON) */}
                  {entry.action_data && Object.keys(entry.action_data).length > 0 && (
                    <div className={styles.entryData}>
                      {Object.entries(entry.action_data).map(([key, value]) => (
                        <div key={key} className={styles.dataItem}>
                          <strong>{translateField(key)}:</strong>
                          <span>{typeof value === 'object' ? JSON.stringify(value) : translateValue(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Imagem */}
                  {entry.image_url && (
                    <div className={styles.entryImage}>
                      <img 
                        src={entry.image_url} 
                        alt="Anexo" 
                        className={styles.imagePreview}
                      />
                    </div>
                  )}

                  {/* Autor */}
                  <div className={styles.entryAuthor}>
                    {renderAuthorAvatar(entry)}
                    <div className={styles.authorInfo}>
                      <span className={styles.authorName}>
                        {entry.creator_name || 'Sistema'}
                      </span>
                      {entry.creator_email && (
                        <span className={styles.authorEmail}>{entry.creator_email}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Loading mais histórico */}
        {loading && (
          <div className={styles.loadingMore}>
            <Spinner animation="border" size="sm" variant="primary" />
            <span>Carregando mais histórico...</span>
          </div>
        )}

        <div ref={timelineEndRef} />
      </div>

      {/* Modal do Editor de Comentarios */}
      {canAddComments && (
        <Modal
          show={showEditorModal}
          onHide={() => setShowEditorModal(false)}
          centered
          size="lg"
          backdrop={false}
          enforceFocus={false}
        >
          <Modal.Header closeButton>
            <Modal.Title as="span" style={{ fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon="mdi:pencil-plus" />
              Adicionar Entrada ao Historico
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SaleHistoryEditor
              saleId={saleId}
              onSuccess={() => {
                setShowEditorModal(false);
                if (onActionSuccess) onActionSuccess();
              }}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default SaleHistoryTimeline;

